/*
 * Shared Klaviyo sign-up helper (client API, public key only), loaded once in layout/theme.liquid.
 * Every website email sign-up joins the same list and gets the same welcome flow.
 * Forms opt in with `data-klaviyo-signup`; optional `data-success` and `data-source`.
 */
(() => {
  if (window.taelKlaviyo) return;

  const API = 'https://a.klaviyo.com/client/';
  const REVISION = '2025-01-15';
  const POPUP_KEY = 'tael-welcome-popup';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const tag = document.querySelector('script[src*="klaviyo-subscribe"][data-company-id]');
  const config = {
    companyId: tag?.dataset.companyId || '',
    listId: tag?.dataset.listId || '',
    success: tag?.dataset.success || '',
  };

  function post(companyId, endpoint, data) {
    return fetch(`${API}${endpoint}/?company_id=${encodeURIComponent(companyId || config.companyId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify({ data }),
    }).then((res) => {
      if (!res.ok) throw new Error(`Klaviyo ${endpoint} ${res.status}`);
    });
  }

  function track(metric, properties = {}) {
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['track', metric, properties]);
  }

  function markSubscribed() {
    try {
      const state = JSON.parse(localStorage.getItem(POPUP_KEY)) || {};
      localStorage.setItem(POPUP_KEY, JSON.stringify({ ...state, subscribed: true }));
    } catch (e) {}
  }

  async function subscribe({ email, companyId, listId, source }) {
    await post(companyId, 'subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: source || 'Website sign-up form',
        profile: {
          data: {
            type: 'profile',
            attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: listId || config.listId } } },
    });
    markSubscribed();
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['identify', { email }]);
  }

  function updateProfile({ email, companyId, firstName, properties = {} }) {
    const attributes = { email, properties };
    if (firstName) attributes.first_name = firstName;
    return post(companyId, 'profiles', { type: 'profile', attributes });
  }

  /** Submits any email form to Klaviyo and shows the result inside the form. */
  async function submitForm(form, { companyId, listId, source, success } = {}) {
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"], button:not([type])');
    let message = form.querySelector('[data-klaviyo-message]');
    if (!message) {
      message = document.createElement('p');
      message.setAttribute('data-klaviyo-message', '');
      message.setAttribute('role', 'status');
      message.tabIndex = -1;
      form.append(message);
    }
    // preventScroll keeps the visitor exactly where they are when the message appears
    const show = (text) => {
      message.textContent = text;
      message.hidden = false;
      message.focus({ preventScroll: true });
    };

    const email = input ? input.value.trim() : '';
    if (!EMAIL_RE.test(email)) {
      input?.setAttribute('aria-invalid', 'true');
      show('Please enter a valid email address.');
      return;
    }
    input.removeAttribute('aria-invalid');
    if (button) button.disabled = true;
    try {
      await subscribe({ email, companyId, listId, source });
      track('Signed up via website form', { source: source || 'Website sign-up form' });
      // Hold the form's height so nothing below moves, then swap the fields for the thank-you card
      form.style.minHeight = `${form.offsetHeight}px`;
      [...form.children].forEach((el) => {
        if (el !== message && el.type !== 'hidden') {
          el.hidden = true;
          el.style.display = 'none';
        }
      });
      form.classList.add('klaviyo-form--done');
      message.classList.add('klaviyo-success');
      show(config.success || success || 'Welcome to the pack! Your 10% code is on its way to your inbox.');
    } catch (err) {
      show('Something went wrong. Please try again.');
    } finally {
      if (button) button.disabled = false;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-klaviyo-signup')) return;
    event.preventDefault();
    submitForm(form, { source: form.dataset.source, success: form.dataset.success });
  });

  window.taelKlaviyo = { config, isValidEmail: (email) => EMAIL_RE.test(email), track, subscribe, updateProfile, submitForm };
})();
